import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiDelete, apiUpload } from '../api';

const BannerUpload = () => {
  const [bannerName, setBannerName] = useState('');
  const [bannerLink, setBannerLink] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [uploadedBanners, setUploadedBanners] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  const fetchBanners = useCallback(async () => {
    try {
      const banners = await apiGet('/api/banners/all');
      setUploadedBanners(banners);
    } catch (e) {
      setErrorText(`Failed to load banners: ${e.message}`);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImageFile(null); setImagePreview(null); setStatusText(''); setErrorText('');
    if (file && (file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/gif")) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => { setImagePreview(reader.result); setStatusText('Image ready for upload.'); };
      reader.readAsDataURL(file);
    } else if (file) {
      setErrorText('Invalid file type. Please select a PNG, JPG, or GIF file.');
    }
  };

  const uploadBanner = async (e) => {
    e.preventDefault();
    if (!bannerName || !imageFile) { setErrorText('Please provide a banner name and select an image file.'); return; }
    setLoading(true); setErrorText('');
    try {
      setStatusText('Uploading banner image...');
      const { url: imageUrl } = await apiUpload('banners', imageFile, `${Date.now()}_${imageFile.name}`);
      setStatusText('Saving to database...');
      await apiPost('/api/banners', { name: bannerName, link: bannerLink, imageUrl });
      alert('Banner uploaded successfully!');
      setBannerName(''); setBannerLink(''); setImageFile(null); setImagePreview(null); setStatusText(''); setErrorText('');
      if (document.getElementById('file-input')) document.getElementById('file-input').value = '';
      fetchBanners();
    } catch (error) {
      setErrorText(`Upload failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (banner) => {
    if (!window.confirm(`Delete banner "${banner.name}"? This cannot be undone.`)) return;
    try {
      await apiDelete(`/api/banners/${banner.id}`);
      alert('Banner deleted successfully!');
      fetchBanners();
    } catch (error) {
      alert(`Failed to delete: ${error.message}`);
    }
  };

  return (
    <div className="dark-page">
      {/* Upload Form */}
      <div className="dark-upload-card">
        <h3>Upload New Banner 🖼️</h3>
        <form onSubmit={uploadBanner}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <label htmlFor="bannerName">Banner Name</label>
              <input type="text" id="bannerName" value={bannerName} onChange={e => setBannerName(e.target.value)} required placeholder="e.g. Promo Banner (320x50 px)" />
              <label htmlFor="bannerLink">Banner Link (Optional)</label>
              <input type="url" id="bannerLink" value={bannerLink} onChange={e => setBannerLink(e.target.value)} placeholder="https://example.com/offer" />
              <label htmlFor="file-input">Banner Image (PNG, JPG, GIF)</label>
              <input type="file" id="file-input" accept="image/png,image/jpeg,image/gif" onChange={handleFileChange} required />
            </div>
            <div className="preview-zone">
              {imagePreview ? (
                <div style={{ textAlign: 'center', width: '100%' }}>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 10 }}>Image Preview</p>
                  <img src={imagePreview} alt="Banner preview" style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }} />
                </div>
              ) : (
                <>
                  <i className="fas fa-image" style={{ fontSize: 36, marginBottom: 10, opacity: 0.3 }} />
                  <p>Select an image to see preview</p>
                </>
              )}
            </div>
          </div>
          {statusText && <p className="status-blue">{statusText}</p>}
          {errorText && <div className="status-error">{errorText}</div>}
          <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
            <button type="submit" className="btn-upload" disabled={loading || !imagePreview}>
              {loading ? <><i className="fas fa-spinner fa-spin" /> Uploading...</> : <><i className="fas fa-upload" /> Upload Banner</>}
            </button>
          </div>
        </form>
      </div>

      {/* Banner List */}
      <h4 className="section-heading">Uploaded Banners</h4>
      {listLoading ? (
        <div className="dark-loading"><div className="spinner" /><p>Loading banners...</p></div>
      ) : uploadedBanners.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'rgba(255,255,255,0.3)' }}>
          <i className="fas fa-image" style={{ fontSize: 36, marginBottom: 10, display: 'block' }} />
          No banners uploaded yet.
        </div>
      ) : (
        <div className="dark-grid dark-grid-wide">
          {uploadedBanners.map(banner => (
            <div key={banner.id} className="dark-grid-item" style={{ aspectRatio: 'auto', minHeight: 120 }}>
              <img src={banner.imageUrl} alt={banner.name} style={{ width: '100%', height: 90, objectFit: 'contain' }} />
              <div className="item-name" title={banner.name}>{banner.name}</div>
              {banner.link && (
                <a href={banner.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: '#a78bfa', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90%' }}>
                  {banner.link.replace(/^https?:\/\//, '')}
                </a>
              )}
              <button className="delete-btn" onClick={() => handleDelete(banner)} title="Delete">
                <i className="fas fa-times" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerUpload;
